import { useEffect } from "react";
import { CANONICAL_ORIGIN } from "@/lib/canonical";

interface PageSEOProps {
  title: string;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: string;
  siteName?: string;
  noindex?: boolean;
}

const removeMeta = (nameOrProp: string, isProperty?: boolean) => {
  const attr = isProperty ? "property" : "name";
  const el = document.querySelector(`meta[${attr}="${nameOrProp}"]`);
  if (el) el.remove();
};

export default function PageSEO({
  title,
  description,
  canonicalPath,
  ogImage,
  ogType = "website",
  siteName = "Legendary Log Cabin on Lake Miltona",
  noindex = false,
}: PageSEOProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (nameOrProp: string, content: string, isProperty?: boolean) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${nameOrProp}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, nameOrProp);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta("description", description);

    if (noindex) {
      setMeta("robots", "noindex, nofollow");
    } else {
      removeMeta("robots");
    }

    const origin = CANONICAL_ORIGIN;

    const absoluteImage = ogImage
      ? ogImage.startsWith("http")
        ? ogImage
        : `${origin}${ogImage.startsWith("/") ? "" : "/"}${ogImage}`
      : null;

    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:type", ogType, true);
    setMeta("og:site_name", siteName, true);
    setMeta("og:locale", "en_US", true);

    if (canonicalPath) {
      const fullUrl = `${origin}${canonicalPath}`;
      setMeta("og:url", fullUrl, true);

      let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.rel = "canonical";
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.href = fullUrl;
    }

    if (absoluteImage) {
      setMeta("og:image", absoluteImage, true);
      setMeta("twitter:card", "summary_large_image");
      setMeta("twitter:image", absoluteImage);
    } else {
      removeMeta("og:image", true);
      removeMeta("twitter:image");
      setMeta("twitter:card", "summary");
    }

    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    setMeta("geo.region", "US-MN");
    setMeta("geo.placename", "Miltona, Minnesota");

    return () => {
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
      removeMeta("robots");
      removeMeta("og:image", true);
      removeMeta("twitter:image");
      removeMeta("og:locale", true);
    };
  }, [title, description, canonicalPath, ogImage, ogType, siteName, noindex]);

  return null;
}
